import { useEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n';
import { Button, Modal } from './ui';

/**
 * Fetches a PDF from the API (cookie session, so plain fetch with credentials)
 * and hands back an object URL to show it inline. Used for concept invoices,
 * which are rendered on the fly and never stored, and for the archived PDF on
 * the invoice page. The URL is revoked when it is replaced or on unmount, so a
 * rendered PDF is not pinned in memory for the tab's lifetime.
 */
export function usePdfObjectUrl(url: string): { objectUrl: string | null; error: string | null } {
  const { lang } = useI18n();
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const current = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setObjectUrl(null); setError(null);
    (async () => {
      try {
        const sep = url.includes('?') ? '&' : '?';
        const res = await fetch(`${url}${sep}lang=${lang}`, { credentials: 'include' });
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          throw new Error((body as { error?: string } | null)?.error ?? res.statusText);
        }
        const blob = await res.blob();
        if (cancelled) return;
        if (current.current) URL.revokeObjectURL(current.current);
        current.current = URL.createObjectURL(blob);
        setObjectUrl(current.current);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => { cancelled = true; };
  }, [url, lang]);

  useEffect(() => () => { if (current.current) URL.revokeObjectURL(current.current); }, []);
  return { objectUrl, error };
}

/** The PDF itself, in a frame, with the loading and error states around it. */
export function PdfFrame({ url, title, height = '70vh' }: { url: string; title: string; height?: string }) {
  const { t } = useI18n();
  const { objectUrl, error } = usePdfObjectUrl(url);
  if (error) return <div className="banner tint-danger"><i className="fas fa-circle-exclamation" aria-hidden="true" style={{ color: 'var(--danger)' }} /><span>{error}</span></div>;
  if (!objectUrl) return <div className="loading">{t('conceptRendering')}</div>;
  return <iframe title={title} src={objectUrl} style={{ width: '100%', height, border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-xs)', background: '#fff' }} />;
}

/** The concept-invoice dialog. */
export function PdfPreview({ url, title, onClose }: { url: string; title: string; onClose: () => void }) {
  const { t } = useI18n();
  const { objectUrl, error } = usePdfObjectUrl(url);

  return (
    <Modal title={title} onClose={onClose}>
      <div className="stack md">
        {error
          ? <div className="banner tint-danger"><i className="fas fa-circle-exclamation" aria-hidden="true" style={{ color: 'var(--danger)' }} /><span>{error}</span></div>
          : objectUrl
            ? <iframe title={title} src={objectUrl} style={{ width: '100%', height: '70vh', border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-xs)', background: '#fff' }} />
            : <div className="loading">{t('conceptRendering')}</div>}
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          {objectUrl && <Button icon="fas fa-arrow-up-right-from-square" onClick={() => window.open(objectUrl, '_blank')}>{t('openInNewTab')}</Button>}
          <Button variant="primary" onClick={onClose}>{t('done')}</Button>
        </div>
      </div>
    </Modal>
  );
}
